export type TicketColumnId =
  | 'number'
  | 'subject'
  | 'company'
  | 'contact'
  | 'queue'
  | 'status'
  | 'priority'
  | 'assignee'
  | 'channel'
  | 'category'
  | 'created'
  | 'updated'
  | 'resolutionDue';

export type TicketTableColumn = {
  id: TicketColumnId;
  label: string;
  description: string;
  defaultWidth: number;
  minWidth: number;
  maxWidth: number;
  defaultVisible: boolean;
  required?: boolean;
};

export type TicketTableSettings = {
  visible: TicketColumnId[];
  widths: Record<TicketColumnId, number>;
};

export const TICKET_TABLE_SETTINGS_STORAGE_KEY = 'ihd-ticket-table-settings-v1';

export const TICKET_TABLE_COLUMNS: TicketTableColumn[] = [
  { id: 'number', label: 'Номер', description: 'Идентификатор заявки', defaultWidth: 92, minWidth: 72, maxWidth: 180, defaultVisible: true, required: true },
  { id: 'subject', label: 'Тема', description: 'Тема и направление обращения', defaultWidth: 420, minWidth: 150, maxWidth: 760, defaultVisible: true },
  { id: 'company', label: 'Компания', description: 'Компания заявителя', defaultWidth: 220, minWidth: 140, maxWidth: 460, defaultVisible: true },
  { id: 'contact', label: 'Контакт', description: 'Контактное лицо или автор', defaultWidth: 210, minWidth: 140, maxWidth: 420, defaultVisible: false },
  { id: 'queue', label: 'Очередь', description: 'Очередь обработки', defaultWidth: 180, minWidth: 130, maxWidth: 360, defaultVisible: false },
  { id: 'status', label: 'Статус', description: 'Текущий этап обработки', defaultWidth: 170, minWidth: 130, maxWidth: 260, defaultVisible: true },
  { id: 'priority', label: 'Приоритет', description: 'Уровень срочности', defaultWidth: 145, minWidth: 112, maxWidth: 230, defaultVisible: true },
  { id: 'assignee', label: 'Исполнитель', description: 'Основной ответственный', defaultWidth: 210, minWidth: 145, maxWidth: 420, defaultVisible: true },
  { id: 'channel', label: 'Канал', description: 'Источник обращения', defaultWidth: 140, minWidth: 110, maxWidth: 260, defaultVisible: false },
  { id: 'category', label: 'Категория', description: 'Категория работ', defaultWidth: 190, minWidth: 135, maxWidth: 380, defaultVisible: false },
  { id: 'created', label: 'Создана', description: 'Дата создания заявки', defaultWidth: 155, minWidth: 125, maxWidth: 250, defaultVisible: false },
  { id: 'updated', label: 'Обновлена', description: 'Последнее изменение', defaultWidth: 155, minWidth: 125, maxWidth: 250, defaultVisible: true },
  { id: 'resolutionDue', label: 'Срок SLA', description: 'Срок решения по SLA', defaultWidth: 170, minWidth: 135, maxWidth: 280, defaultVisible: false },
];

const columnById = new Map(TICKET_TABLE_COLUMNS.map(column => [column.id, column]));

function clampWidth(column: TicketTableColumn, value: unknown) {
  const numeric = typeof value === 'number' && Number.isFinite(value) ? Math.round(value) : column.defaultWidth;
  return Math.min(column.maxWidth, Math.max(column.minWidth, numeric));
}

function defaultSettings(): TicketTableSettings {
  return {
    visible: TICKET_TABLE_COLUMNS.filter(column => column.defaultVisible).map(column => column.id),
    widths: Object.fromEntries(TICKET_TABLE_COLUMNS.map(column => [column.id, column.defaultWidth])) as Record<TicketColumnId, number>,
  };
}

export const DEFAULT_TICKET_TABLE_SETTINGS = defaultSettings();

export function normalizeTicketTableSettings(value: unknown): TicketTableSettings {
  if (!value || typeof value !== 'object') return defaultSettings();
  const candidate = value as { visible?: unknown; widths?: unknown };
  const requestedVisible = Array.isArray(candidate.visible)
    ? new Set(candidate.visible.filter((id): id is TicketColumnId => typeof id === 'string' && columnById.has(id as TicketColumnId)))
    : new Set(DEFAULT_TICKET_TABLE_SETTINGS.visible);
  TICKET_TABLE_COLUMNS.filter(column => column.required).forEach(column => requestedVisible.add(column.id));

  const requestedWidths = candidate.widths && typeof candidate.widths === 'object'
    ? candidate.widths as Partial<Record<TicketColumnId, unknown>>
    : {};

  return {
    visible: TICKET_TABLE_COLUMNS.filter(column => requestedVisible.has(column.id)).map(column => column.id),
    widths: Object.fromEntries(TICKET_TABLE_COLUMNS.map(column => [column.id, clampWidth(column, requestedWidths[column.id])])) as Record<TicketColumnId, number>,
  };
}

export function setTicketColumnVisibility(settings: TicketTableSettings, columnId: TicketColumnId, visible: boolean): TicketTableSettings {
  const column = columnById.get(columnId);
  if (!column || (column.required && !visible)) return settings;
  const visibleColumns = new Set(settings.visible);
  if (visible) visibleColumns.add(columnId);
  else visibleColumns.delete(columnId);
  return normalizeTicketTableSettings({ ...settings, visible: [...visibleColumns] });
}

export function setTicketColumnWidth(settings: TicketTableSettings, columnId: TicketColumnId, width: number): TicketTableSettings {
  const column = columnById.get(columnId);
  if (!column) return settings;
  return {
    ...settings,
    widths: { ...settings.widths, [columnId]: clampWidth(column, width) },
  };
}

export function ticketGridTemplate(settings: TicketTableSettings, columns = TICKET_TABLE_COLUMNS.filter(column => settings.visible.includes(column.id))) {
  return columns.map(column => `minmax(${column.minWidth}px, ${settings.widths[column.id]}fr)`).join(' ');
}
